/**
 * Pure mapping of onboarding state → the post-create setup checklist.
 *
 * Ordering encodes the path to a working first session: an agent
 * credential comes first (without one every session 400s with
 * ``session:no_credential``), then the code source, in the two-step
 * start's order. The code source is optional: only code work needs one,
 * since documents and presentations run without a project; branding and team come next; running the first
 * session is the payoff and comes last. Every step tracks completion,
 * so the setup prompt (nudge + top-bar icon) stays visible until every
 * step is done OR the user dismisses it. Steps the viewer can't do are
 * left out rather than shown as never done: team for a manager, billing
 * for a manager or with billing off. Kept pure + separate from the
 * component so the done-state logic is unit-testable without mocking
 * the React Query hooks the component wires in.
 */

import { AGENT_LOGIN_HREF } from "@/features/account-link/accountLinks";

export type ChecklistState = {
  /** A code source exists for the org. */
  hasSource: boolean;
  /** A usable agent credential is available (active personal link on
   *  any provider, or deployment-wide central/server billing). */
  hasCredential: boolean;
  /** The org has at least one session (not deleted), whoever started it. */
  hasSession: boolean;
  /** The org has set up document branding — keyed on brand ASSETS
   *  (logo / fonts uploaded or generated). Tokens alone aren't a
   *  reliable signal (orgs can carry seeded/partial default tokens). */
  hasBrand: boolean;
  /** The org has started its team — a second member OR a sign-in
   *  (auth-match) rule that auto-adds colleagues. */
  hasTeam: boolean;
  /** The viewer may invite people and set sign-in rules for the org (an
   *  organisation admin, or a superadmin outside it). A manager may not:
   *  the step could never read as done for them, so it is left out. */
  canInviteTeam: boolean;
  /** Billing is switched on for this deployment. When it is off the
   *  billing row is omitted entirely rather than shown as incomplete —
   *  a step nobody can complete reads as a broken checklist. */
  billingEnabled: boolean;
  /** The viewer may open this org's Billing page (an organisation
   *  admin, or a global admin). A manager may not: the row would link
   *  to a page that sends them to Overview, and could never be done. */
  canOpenBilling: boolean;
  /** The org can pay for turns: a credit balance above zero OR a live
   *  agreement. Either alone is enough — a subscription is NOT
   *  mandatory, so an org running on purchased credits is done. */
  hasBillingCover: boolean;
};

export type ChecklistItem = {
  /** i18n suffix: ``onboarding.checklist_<key>_title`` / ``_body``. */
  key: string;
  /** True/false once tracked; every step tracks completion now. */
  done?: boolean;
  /** In-app destination when the row is activated. */
  path: string;
  /** ``true`` for the prerequisites of a working workspace (agent
   *  credential, first session); ``false`` for optional improvements
   *  (code source, branding, team, billing). Drives the welcome-page required-vs-optional grouping;
   *  both groups still count toward the setup prompt's completion. */
  required: boolean;
};

export function buildSetupChecklist(state: ChecklistState): ChecklistItem[] {
  return [
    { key: "credential", done: state.hasCredential, path: AGENT_LOGIN_HREF, required: true },
    { key: "scm", done: state.hasSource, path: "/app/admin/code-sources", required: false },
    { key: "first_session", done: state.hasSession, path: "/app", required: true },
    { key: "brand", done: state.hasBrand, path: "/app/admin/branding", required: false },
    ...(state.canInviteTeam
      ? [{ key: "team", done: state.hasTeam, path: "/app/admin/members", required: false }]
      : []),
    // Optional on purpose. Credits are what a turn actually needs and a
    // plan is not mandatory, so this must never sit between a credential
    // and a first session — it points at how to keep working, not at a
    // gate to get started.
    ...(state.billingEnabled && state.canOpenBilling
      ? [
          {
            key: "billing",
            done: state.hasBillingCover,
            path: "/app/admin/billing",
            required: false,
          },
        ]
      : []),
  ];
}

/** Steps the setup prompt (nudge, top-bar icon) still counts: tracked and
 *  not done. A code source the viewer skipped in the two-step start is
 *  left out, so someone who only makes documents isn't reminded of it
 *  for ever; it stays on the checklist, unticked. */
export function countIncomplete(
  items: readonly ChecklistItem[],
  { sourceSkipped }: { sourceSkipped: boolean },
): number {
  return items.filter((i) => i.done === false && !(i.key === "scm" && sourceSkipped)).length;
}
